import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatCurrency } from '../../utils/formatCurrency';
import { pendingDealsText, RELOADING_MESSAGE } from '../../utils/influenceBlocks';
import { winRateInline } from '../../utils/winRate';

const money = (v) => formatCurrency(v, { emptyDisplay: '—' });

export const kpiBasisActive = (d) => !!(d?.influence_cutover && d?.kpi_basis?.available);

export const basisName = (b) => (b?.definition === 'tec_product_attributed' ? 'TEC Product attributed' : 'TEC Program influenced');

export function basisLineText(b) {
  if (b.period_filter_applied) {
    return `Deals created ${b.created_from || '—'} to ${b.created_to || '—'} · outcomes as of ${b.as_of || '—'}`;
  }
  return `Deals created since ${b.created_from || '—'} · as of ${b.as_of || '—'}`;
}

const FALLBACK_REASONS = {
  period_not_precomputed: 'this period is not precomputed (custom range, or a new quarter before tonight\'s run)',
  event_filter_not_supported: 'the event filter is not supported',
  product_filter_not_supported: 'the product filter is not supported',
  segment_not_supported: 'participant segments are not supported',
};

// Cutover is on but the influenced numbers cannot answer this view: the card keeps the previous attendee-linked numbers.
export function basisFallbackText(d) {
  const b = d?.kpi_basis;
  if (!d?.influence_cutover || !b || b.available) return null;
  if (b.reason === 'influence_reloading') return b.message || RELOADING_MESSAGE;
  return `Previous attendee-linked definition: ${FALLBACK_REASONS[b.reason] || 'no influence scorecard rows for this view'}`;
}

// Win rate on the card is the raw (unadjusted) rate over closed deals; the adjusted lift lives in the influence scorecard.
export function basisWinRateText(b) {
  const p = b?.program;
  if (!p || p.win_rate_pct == null) return 'Win rate — (raw, unadjusted)';
  const open = p.open_share_pct;
  return `Raw win rate ${winRateInline(p.win_rate_pct / 100, { n: p.n_closed, openShare: open == null ? undefined : open / 100 })}, unadjusted`;
}

export function basisTooltip(b, base) {
  return [base, b?.note].filter(Boolean).join(' ');
}

const TIERS = [
  ['program', 'TEC Program influenced (broader tier)'],
  ['deep_dive', 'Deep Dive / Product attributed tier'],
  ['customer_account', 'Customer-account path (separate)'],
  ['partner', 'Partner path (separate)'],
];

export function BasisSubLines({ basis, field = 'value' }) {
  const mainTier = basis?.definition === 'tec_product_attributed' ? 'deep_dive' : 'program';
  const rows = TIERS.filter(([k]) => k !== mainTier && basis?.[k] && basis[k][field] != null);
  const pending = field === 'value' ? pendingDealsText(basis?.pending_review) : null;
  if (!rows.length && !pending) return null;
  return (
    <Box aria-label="KPI basis tiers">
      {rows.map(([k, label]) => (
        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{money(basis[k][field])}</Typography>
        </Box>
      ))}
      {pending && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{pending}</Typography>}
    </Box>
  );
}

export function BasisLine({ basis }) {
  return <Typography variant="caption" color="text.secondary" aria-label="KPI basis">{basisLineText(basis)}</Typography>;
}
